// SVG renderer for one design-canvas object (shapes, lines, text, image, video).
const starPoints = (w, h) => {
  const cx = w / 2, cy = h / 2, R = Math.min(w, h) / 2, r = R * 0.42;
  const pts = [];
  for (let i = 0; i < 10; i++) {
    const ang = -Math.PI / 2 + (i * Math.PI) / 5;
    const rad = i % 2 === 0 ? R : r;
    pts.push(`${(cx + rad * Math.cos(ang)).toFixed(1)},${(cy + rad * Math.sin(ang)).toFixed(1)}`);
  }
  return pts.join(" ");
};

export const dashArray = (dash, sw) => {
  const w = Math.max(1, sw);
  if (dash === "dashed") return `${w * 4},${w * 2.5}`;
  if (dash === "dotted") return `${w},${w * 1.8}`;
  return undefined;
};

export const ANIM_CSS = {
  none: {},
  fade: { animation: "hqFadeIn 0.8s ease both" },
  fly: { animation: "hqFlyIn 0.7s ease both" },
  zoom: { animation: "hqZoomIn 0.7s ease both" },
  spin: { animation: "hqSpinIn 0.9s ease both" },
};

export const IS_LINE = (t) => t === "line" || t === "elbow";

export default function DrawShape({ o, gallery = [], videos = [] }) {
  const sw = IS_LINE(o.type) ? Math.max(1, o.strokeWidth || 0) : o.strokeWidth || 0;
  const w = Math.max(2, o.w), h = Math.max(2, o.h);
  const common = {
    fill: o.fill, stroke: sw > 0 ? o.stroke : "none", strokeWidth: sw,
    strokeDasharray: dashArray(o.dash, sw), strokeLinejoin: "round",
  };
  const inset = sw / 2;

  if (o.type === "text") {
    return (
      <div className="w-full h-full flex overflow-hidden px-1" style={{ alignItems: "center", justifyContent: o.align === "left" ? "flex-start" : o.align === "right" ? "flex-end" : "center", background: o.fill === "none" ? "transparent" : o.fill, border: sw > 0 ? `${sw}px ${o.dash === "solid" ? "solid" : o.dash} ${o.stroke}` : "none" }}>
        <span style={{ color: o.color, fontSize: o.fontSize, fontFamily: o.fontFamily, fontWeight: o.bold ? 700 : 400, fontStyle: o.italic ? "italic" : "normal", lineHeight: 1.15, textAlign: o.align, whiteSpace: "pre-wrap", width: "100%" }}>
          {o.text || "Type your text"}
        </span>
      </div>
    );
  }

  if (o.type === "image") {
    const g = gallery.find((x) => x.id === o.imageId);
    if (!g) return <div className="w-full h-full rounded border-2 border-dashed border-slate-400 flex items-center justify-center text-[11px] text-slate-500">Pick an image</div>;
    return <img src={g.url} alt={o.alt || g.label} draggable={false} className="w-full h-full object-cover" style={{ border: sw > 0 ? `${sw}px solid ${o.stroke}` : "none" }} />;
  }

  if (o.type === "video") {
    const v = videos.find((x) => x.id === o.videoId);
    return (
      <div className="w-full h-full relative bg-black overflow-hidden" style={{ border: sw > 0 ? `${sw}px solid ${o.stroke}` : "none" }}>
        {v && <img src={v.poster} alt={v.label} draggable={false} className="w-full h-full object-cover opacity-80" />}
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1">
          <span className="w-9 h-9 rounded-full bg-white/85 flex items-center justify-center text-slate-900 text-lg leading-none pl-0.5">▶</span>
          <span className="text-[10px] text-white/90 px-1 text-center">{v ? v.label : "Pick a video"}</span>
        </div>
      </div>
    );
  }

  const markerId = `hqmk-${o.id}`;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: "block", overflow: "visible" }}>
      {IS_LINE(o.type) && (
        <defs>
          <marker id={markerId} markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto" markerUnits="strokeWidth">
            <path d="M0,0 L6,3 L0,6 z" fill={o.stroke} />
          </marker>
          <marker id={`${markerId}-s`} markerWidth="6" markerHeight="6" refX="1" refY="3" orient="auto" markerUnits="strokeWidth">
            <path d="M6,0 L0,3 L6,6 z" fill={o.stroke} />
          </marker>
        </defs>
      )}
      {o.type === "rect" && <rect x={inset} y={inset} width={Math.max(1, w - sw)} height={Math.max(1, h - sw)} {...common} />}
      {o.type === "roundrect" && <rect x={inset} y={inset} rx={Math.min(w, h) * 0.16} width={Math.max(1, w - sw)} height={Math.max(1, h - sw)} {...common} />}
      {o.type === "ellipse" && <ellipse cx={w / 2} cy={h / 2} rx={Math.max(1, w / 2 - inset)} ry={Math.max(1, h / 2 - inset)} {...common} />}
      {o.type === "triangle" && <polygon points={`${w / 2},${inset} ${w - inset},${h - inset} ${inset},${h - inset}`} {...common} />}
      {o.type === "star" && <polygon points={starPoints(w, h)} {...common} />}
      {o.type === "arrowshape" && <polygon points={`${inset},${h * 0.3} ${w * 0.58},${h * 0.3} ${w * 0.58},${inset} ${w - inset},${h / 2} ${w * 0.58},${h - inset} ${w * 0.58},${h * 0.7} ${inset},${h * 0.7}`} {...common} />}
      {o.type === "bubble" && (
        <g {...common}>
          <rect x={inset} y={inset} rx={14} width={Math.max(1, w - sw)} height={Math.max(1, h * 0.78 - sw)} />
          <polygon points={`${w * 0.22},${h * 0.76} ${w * 0.18},${h - inset} ${w * 0.42},${h * 0.76}`} />
        </g>
      )}
      {o.type === "line" && (
        <line x1={0} y1={h / 2} x2={w} y2={h / 2} fill="none" stroke={o.stroke} strokeWidth={sw} strokeDasharray={dashArray(o.dash, sw)} strokeLinecap="round"
          markerEnd={o.arrowEnd ? `url(#${markerId})` : undefined} markerStart={o.arrowStart ? `url(#${markerId}-s)` : undefined} />
      )}
      {o.type === "elbow" && (
        <polyline points={`0,0 0,${h} ${w},${h}`} fill="none" stroke={o.stroke} strokeWidth={sw} strokeDasharray={dashArray(o.dash, sw)} strokeLinecap="round" strokeLinejoin="round"
          markerEnd={o.arrowEnd ? `url(#${markerId})` : undefined} markerStart={o.arrowStart ? `url(#${markerId}-s)` : undefined} />
      )}
      {!IS_LINE(o.type) && o.text ? (
        <text x={w / 2} y={h / 2} dominantBaseline="middle" textAnchor="middle" fill={o.color} fontSize={o.fontSize} fontFamily={o.fontFamily} fontWeight={o.bold ? 700 : 400} fontStyle={o.italic ? "italic" : "normal"}>{o.text}</text>
      ) : null}
    </svg>
  );
}
